import { useState, useEffect, useRef, useMemo } from 'react'
import { IntlShape } from 'react-intl'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { Modal, Tooltip, OverlayTrigger } from 'react-bootstrap'
import { ID, KTSVG } from '../../../../../../../_dashboard/helpers'
import { getAll, createOne, patchOne, deleteOne } from '../../../../../../services/common_requests'
import DiscountsForm, { TourDiscount } from './DiscountsForm'
import { tierPricePerPersonFromDiscount, tierTotalTTCFromDiscount } from './discountTierSync'
import { useConfirm } from '../../../../../../components/ConfirmProvider'

/** TTC per person used to preview group discount (aligned with tarif général). */
function getReferencePricePerPersonTTC(pricing: any): number | null {
  if (!pricing) return null
  const mode = pricing.pricingMode
  const priceTTC = Number(pricing.priceTTC)
  if (mode === 'PER_PERSON') {
    const ptPrices = pricing.personTypePrices || []
    const fromTypes = ptPrices
      .map((p: any) => Number(p.price ?? p.priceTTC) || 0)
      .filter((n: number) => n > 0)
    if (fromTypes.length > 0) {
      return Math.max(...fromTypes)
    }
    return priceTTC > 0 ? priceTTC : null
  }
  if (mode === 'PER_GROUP') {
    const maxG = Number(pricing.maxGroupSize)
    const gTtc = Number(pricing.groupPriceTTC)
    if (maxG > 0 && gTtc > 0) {
      return gTtc / maxG
    }
    return priceTTC > 0 ? priceTTC : null
  }
  return priceTTC > 0 ? priceTTC : null
}

// Import Metronic menu
declare const KTMenu: any;

interface DiscountsProps {
  tourId: string
  intl: IntlShape
}

const Discounts: React.FC<DiscountsProps> = ({
  tourId,
  intl,
}) => {
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [currentItem, setCurrentItem] = useState<TourDiscount | null>(null)
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [showDropdownId, setShowDropdownId] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const confirm = useConfirm()

  // Prix général (même clé que l’onglet Tarification) pour le calcul prix / pers.
  const { data: tourPricingResponse } = useQuery(
    ['tourPricing', tourId],
    () => getAll(null, `/tour-prices/by-tour/${tourId}`),
    { enabled: !!tourId }
  )

  const referencePricePerPersonTTC = useMemo(
    () => getReferencePricePerPersonTTC(tourPricingResponse?.data),
    [tourPricingResponse?.data]
  )

  // Fetch discounts
  const { data: discountsData, isLoading } = useQuery(
    ['tourDiscounts', tourId],
    () => getAll(null, `/tour-group-discounts/?tourId=${tourId}`),
    {
      enabled: !!tourId,
      retry: 1,
      onError: (error) => {
        console.error("Fetch discounts error:", error)
        toast.error(intl.formatMessage({ id: 'TOUR.DISCOUNTS.FETCH_ERROR' }))
      }
    }
  )

  // Create mutation
  const createMutation = useMutation(
    (data: Omit<TourDiscount, '_id' | 'createdAt' | 'updatedAt'>) =>
      createOne(data, '/tour-group-discounts'),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tourDiscounts', tourId])
        toast.success(intl.formatMessage({ id: 'TOUR.DISCOUNTS.CREATE_SUCCESS' }))
        closeModal()
        setIsSaving(false)
      },
      onError: (error) => {
        console.error("Create error:", error)
        toast.error(intl.formatMessage({ id: 'TOUR.DISCOUNTS.CREATE_ERROR' }))
        setIsSaving(false)
      },
      retry: false
    }
  )

  // Update mutation
  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<TourDiscount> }) =>
      patchOne(data, `/tour-group-discounts/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tourDiscounts', tourId])
        toast.success(intl.formatMessage({ id: 'TOUR.DISCOUNTS.UPDATE_SUCCESS' }))
        closeModal()
        setIsSaving(false)
      },
      onError: (error) => {
        console.error("Update error:", error)
        toast.error(intl.formatMessage({ id: 'TOUR.DISCOUNTS.UPDATE_ERROR' }))
        setIsSaving(false)
      },
      retry: false
    }
  )

  // Delete mutation
  const deleteMutation = useMutation(
    (id: ID) => deleteOne(id, `/tour-group-discounts`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['tourDiscounts', tourId])
        toast.success(intl.formatMessage({ id: 'TOUR.DISCOUNTS.DELETE_SUCCESS' }))
      },
      onError: (error) => {
        console.error("Delete error:", error)
        toast.error(intl.formatMessage({ id: 'TOUR.DISCOUNTS.DELETE_ERROR' }))
      },
    }
  )

  // Handle form submission
  const handleSubmit = (values: TourDiscount) => {
    setIsSaving(true)
    
    // Ensure all numeric values are numbers, not strings
    const minParticipants = Number(values.minParticipants)
    const discountValue = Number(values.discountValue)
    
    // Ensure dates are properly formatted
    const startDate = values.startDate ? new Date(values.startDate).toISOString() : new Date().toISOString()
    const endDate = values.endDate ? new Date(values.endDate).toISOString() : new Date().toISOString()
    
    const payload = {
      ...values,
      tour: tourId,
      minParticipants,
      discountValue,
      startDate,
      endDate,
    }

    if (currentItem?._id) {
      updateMutation.mutate({
        id: currentItem._id,
        data: payload,
      })
    } else {
      createMutation.mutate(payload as Omit<TourDiscount, '_id' | 'createdAt' | 'updatedAt'>)
    }
  }

  // Handle opening the modal for adding a new item
  const handleAddNew = () => {
    setCurrentItem({
      tour: tourId,
      minParticipants: 5,
      discountType: 'fixed_amount',
      discountValue: 0,
      startDate: '',
      endDate: '',
      isActive: true,
      displayAsDiscount: false,
    })
    setModalOpen(true)
  }

  // Handle opening the modal for editing an existing item
  const handleEdit = (item: TourDiscount) => {
    setCurrentItem(item)
    setModalOpen(true)
    setShowDropdownId(null)
  }

  // Close modal and reset state
  const closeModal = () => {
    setModalOpen(false)
    setCurrentItem(null)
  }

  // Toggle dropdown visibility
  const toggleDropdown = (id: string) => {
    setShowDropdownId(showDropdownId === id ? null : id)
  }

  // Handle delete
  const handleDelete = async (id: string) => {
    const ok = await confirm({ message: intl.formatMessage({ id: 'TOUR.DISCOUNTS.DELETE_CONFIRM' }) })
    if (!ok) return
    deleteMutation.mutate(id as unknown as ID)
    setShowDropdownId(null)
  }

  // Format date for display
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString()
  }

  const formatMoney = (v: number) =>
    new Intl.NumberFormat(intl.locale || undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(v)

  const listTierPreview = (discount: TourDiscount) => {
    if (discount.discountType === 'fixed_amount' && referencePricePerPersonTTC != null) {
      const total = tierTotalTTCFromDiscount(
        referencePricePerPersonTTC,
        discount.minParticipants,
        Number(discount.discountValue)
      )
      const perPerson = tierPricePerPersonFromDiscount(
        referencePricePerPersonTTC,
        discount.minParticipants,
        Number(discount.discountValue)
      )
      if (total != null && perPerson != null) {
        return { total, perPerson }
      }
    }
    return null
  }

  // Format discount value based on type
  const formatDiscountValue = (discount: TourDiscount) => {
    return discount.discountType === 'percentage'
      ? `${discount.discountValue}%`
      : `${discount.discountValue}€`
  }

  // Initialize Metronic menu
  useEffect(() => {
    // Initialize all dropdown menus
    const menuElements = document.querySelectorAll('[data-kt-menu="true"]')
    menuElements.forEach((menu) => {
      if (typeof KTMenu !== 'undefined') {
        const menuInstance = new KTMenu(menu)
        menuInstance.on('kt.menu.dropdown.show', function() {
          // Handle menu show
        })
      }
    })
  }, [discountsData]) // Re-initialize when data changes

  // Add click outside handler
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdownId(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Render the list view
  const renderListView = () => {
    const items = discountsData?.data || []
    
    // Sort by minimum participants (ascending)
    const sortedItems = [...items].sort((a, b) => a.minParticipants - b.minParticipants)

    return (
      <>
        <div className='d-flex justify-content-end mb-6'>
          <button
            type='button'
            className='btn btn-primary'
            onClick={handleAddNew}
          >
            <i className='ki-solid ki-plus fs-2'></i>
            {intl.formatMessage({ id: 'TOUR.DISCOUNTS.ADD' })}
          </button>
        </div>

        {sortedItems.length === 0 ? (
          <div className='text-center py-10'>
            <h3 className='fs-4 fw-bold mb-5'>
              {intl.formatMessage({ id: 'TOUR.DISCOUNTS.NO_DISCOUNTS' })}
            </h3>
            <p className='fs-6 text-gray-700'>
              {intl.formatMessage({ id: 'TOUR.DISCOUNTS.ADD_DESCRIPTION' })}
            </p>
          </div>
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-5'>
              <thead>
                <tr className='text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0'>
                  <th>{intl.formatMessage({ id: 'TOUR.DISCOUNTS.MIN_PARTICIPANTS' })}</th>
                  <th>{intl.formatMessage({ id: 'TOUR.DISCOUNTS.VALUE' })}</th>
                  <th>{intl.formatMessage({ id: 'TOUR.DISCOUNTS.LIST_TOTAL_TIER_TTC' })}</th>
                  <th>{intl.formatMessage({ id: 'TOUR.DISCOUNTS.LIST_PRICE_PER_PERSON_TTC' })}</th>
                  <th>{intl.formatMessage({ id: 'TOUR.DISCOUNTS.DATE_RANGE' })}</th>
                  <th>{intl.formatMessage({ id: 'TOUR.DISCOUNTS.ACTIVE_STATUS' })}</th>
                  <th className='text-end'>{intl.formatMessage({ id: 'COMMON.ACTIONS' })}</th>
                </tr>
              </thead>
              <tbody className='fw-semibold text-gray-600'>
                {sortedItems.map((item) => {
                  const tierPreview = listTierPreview(item)
                  return (
                  <tr key={item._id}>
                    <td>{item.minParticipants}</td>
                    <td>
                      <span className={item.discountType === 'percentage' ? 'badge badge-light-success' : 'badge badge-light-primary'}>
                        {formatDiscountValue(item)}
                      </span>
                    </td>
                    <td>
                      {tierPreview ? (
                        <span>{formatMoney(tierPreview.total)} €</span>
                      ) : (
                        <span className='text-muted'>—</span>
                      )}
                    </td>
                    <td>
                      {tierPreview ? (
                        <span>{formatMoney(tierPreview.perPerson)} €</span>
                      ) : (
                        <span className='text-muted'>—</span>
                      )}
                    </td>
                    <td>
                      {formatDate(item.startDate)} - {formatDate(item.endDate)}
                    </td>
                    <td>
                      <span className={`badge badge-light-${item.isActive ? 'success' : 'danger'}`}>
                        {item.isActive
                          ? intl.formatMessage({ id: 'TOUR.DISCOUNTS.ACTIVE' })
                          : intl.formatMessage({ id: 'TOUR.DISCOUNTS.INACTIVE' })}
                      </span>
                    </td>
                    <td className='text-end'>
                      <OverlayTrigger
                        placement='top'
                        overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.EDIT'})}</Tooltip>}
                      >
                        <button
                          className='btn btn-icon btn-light-primary btn-sm me-1'
                          onClick={() => handleEdit(item)}
                        >
                          <KTSVG path='/media/icons/duotune/art/art005.svg' className='svg-icon-3' />
                        </button>
                      </OverlayTrigger>
                      <OverlayTrigger
                        placement='top'
                        overlay={<Tooltip>{intl.formatMessage({id: 'ACTION.DELETE'})}</Tooltip>}
                      >
                        <button
                          className='btn btn-icon btn-light-danger btn-sm'
                          onClick={() => handleDelete(item._id!)}
                        >
                          <KTSVG path='/media/icons/duotune/general/gen027.svg' className='svg-icon-3' />
                        </button>
                      </OverlayTrigger>
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </>
    )
  }

  if (isLoading) {
    return (
      <div className='d-flex justify-content-center align-items-center min-h-300px'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  return (
    <div className='card card-flush'>
      <div className='card-body'>
        {renderListView()}

        {/* Modal for adding/editing discounts */}
        <Modal
          show={modalOpen}
          onHide={closeModal}
          size='lg'
          centered
          backdrop='static'
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {currentItem?._id
                ? intl.formatMessage({ id: 'TOUR.DISCOUNTS.EDIT' })
                : intl.formatMessage({ id: 'TOUR.DISCOUNTS.ADD' })}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {currentItem && (
              <DiscountsForm
                key={currentItem._id || 'new-discount'}
                initialValues={currentItem}
                onSubmit={handleSubmit}
                onCancel={closeModal}
                intl={intl}
                tourId={tourId}
                isSubmitting={isSaving}
                referencePricePerPersonTTC={referencePricePerPersonTTC}
              />
            )}
          </Modal.Body>
        </Modal>
      </div>
    </div>
  )
}

export default Discounts 